import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useBcv } from '../context/BcvContext';
import { SearchBar } from '../components/SearchBar';
import { SizeFilters } from '../components/SizeFilters';
import { ImageCarousel } from '../components/ImageCarousel';
import { ProductLightbox } from '../components/ProductLightbox';
import { MainHeroBanner } from '../components/MainHeroBanner';
import { Footer } from '../components/Footer';
import { toast } from 'sonner';
import { Instagram, MessageCircle, Trophy, ShoppingBag, Star, LayoutGrid, Shirt, CloudSnow, Dribbble } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const getTotalStock = (tallas) => {
  if (!tallas || typeof tallas !== 'object') return 0;
  return Object.values(tallas).reduce((sum, v) => sum + (v || 0), 0);
};

export default function CatalogPage() {
  const navigate = useNavigate();
  const { addToCart, getCartCount } = useCart();
  const { toBolivares } = useBcv();
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSize, setSelectedSize] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showOferta, setShowOferta] = useState(false);
  const [showDescuento, setShowDescuento] = useState(false);
  const [lightboxProduct, setLightboxProduct] = useState(null);
  const [activeGroup, setActiveGroup] = useState('all');
  const location = useLocation();
  const cartCount = getCartCount();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const cat = params.get('cat');
    if (cat === 'oferta') {
      setShowOferta(true);
      setShowDescuento(false);
      setActiveGroup('oferta');
    } else if (cat === 'descuento') {
      setShowDescuento(true);
      setShowOferta(false);
      setActiveGroup('descuento');
    } else if (cat) {
      setActiveGroup(cat);
      setShowOferta(false);
      setShowDescuento(false);
    }
  }, [location]);
  
  // La URL es la fuente de verdad del modal: abre/cierra el lightbox según ?prodId=
  // Esto permite links únicos por producto (compartibles) y que funcione atrás/adelante.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const prodId = params.get('prodId');
    if (prodId) {
      if (products.length > 0) {
        const product = products.find(p => p.id === prodId);
        setLightboxProduct(product || null);
      }
    } else {
      setLightboxProduct(null);
    }
  }, [location.search, products]);

  // Abrir un producto => escribe ?prodId en la URL (link único, compartible)
  const openLightbox = useCallback((product) => {
    if (!product) return;
    const params = new URLSearchParams(location.search);
    params.set('prodId', product.id);
    navigate(`${location.pathname}?${params.toString()}`);
  }, [navigate, location.search, location.pathname]);

  // Cerrar => quita ?prodId de la URL
  const closeLightbox = useCallback(() => {
    const params = new URLSearchParams(location.search);
    params.delete('prodId');
    const qs = params.toString();
    navigate(qs ? `${location.pathname}?${qs}` : location.pathname);
  }, [navigate, location.search, location.pathname]);

  const fetchProducts = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/products`);
      if (response.ok) {
        const data = await response.json();
        setProducts(data || []);
        setFilteredProducts(data || []);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
    const queryParams = new URLSearchParams(window.location.search);
    const cat = queryParams.get('cat');
    if (cat === 'oferta') setShowOferta(true);
    if (cat === 'descuento') setShowDescuento(true);
  }, [fetchProducts]);

  const applyFilters = useCallback(() => {
    let filtered = [...products];

    // 1. Filtrar por Grupo Principal (activeGroup) si no es 'all'
    if (activeGroup !== 'all' && activeGroup !== 'oferta') {
      const currentGroup = categoryGroups.find(g => g.id === activeGroup);
      if (currentGroup && currentGroup.items) {
        filtered = filtered.filter(p => currentGroup.items.includes(p.categoria));
      }
    }

    // 2. Filtrar por Subcategoría específica
    if (selectedCategory) {
      filtered = filtered.filter(p => p.categoria === selectedCategory);
    }

    // 3. Filtrar por Ofertas y Descuentos
    if (showOferta) {
      filtered = filtered.filter(p => p.oferta === true);
    }
    
    if (showDescuento) {
      filtered = filtered.filter(p => p.descuento > 0);
    }
    
    // 4. Otros filtros (Buscador y Talla)
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(p => p.descripcion.toLowerCase().includes(q));
    }

    if (selectedSize) {
      filtered = filtered.filter(p => p.tallas && p.tallas[selectedSize] > 0);
    }

    filtered.sort((a, b) => a.descripcion.localeCompare(b.descripcion));
    setFilteredProducts(filtered);
  }, [products, searchQuery, selectedSize, selectedCategory, showOferta, showDescuento, activeGroup]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const handleAddToCart = (product, size) => {
    const tallas = product.tallas || {};
    const stock = tallas[size] || 0;
    if (stock === 0) { toast.error('Talla agotada'); return; }
    addToCart({ ...product, selectedSize: size });
    toast.success('Agregado al carrito');
  };

  const categoryGroups = [
    { id: 'all', label: 'TODOS', icon: <LayoutGrid className="w-4 h-4" />, items: [null] },
    { id: 'sports', label: 'FÚTBOL', icon: <Trophy className="w-4 h-4" />, items: [null, 'Adulto', 'Franelillas', 'Dama', 'Niños', 'Uniforme', 'Balón', 'Shorts'] },
    { id: 'nba', label: 'NBA', icon: <Dribbble className="w-4 h-4" />, items: [null, 'Nba'] },
    { id: 'leagues', label: 'OTROS DEPORTES', icon: <Star className="w-4 h-4" />, items: [null, 'Mlb', 'Nfl', 'Lvbp', 'F1'] },
    { id: 'urban', label: 'URBANO', icon: <Shirt className="w-4 h-4" />, items: [null, 'Camisetas Urbanas', 'Pantalones Urbanas', 'Bermudas Urbanas'] },
    { id: 'caps', label: 'GORRAS', icon: <ShoppingBag className="w-4 h-4" />, items: [null, 'Gorras'] },
    { id: 'winter', label: 'FRÍO', icon: <CloudSnow className="w-4 h-4" />, items: [null, 'Chaquetas', 'Suéter'] },
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white relative overflow-x-hidden">
      {/* Fondo de Textura Premium (Fijo) */}
      <div className="fixed inset-0 z-0 opacity-20 pointer-events-none">
        <img 
          src="https://images.unsplash.com/photo-1550684848-fac1c5b4e853?q=80&w=2400" 
          alt="Texture" 
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A] via-transparent to-[#0A0A0A]"></div>
      </div>
      
      {/* Botón Flotante para Volver al Inicio */}
      <button 
        onClick={() => navigate('/')} 
        className="fixed top-4 left-4 z-50 flex items-center gap-2 bg-black/60 backdrop-blur-md border border-gray-700 text-white px-4 py-2 rounded-full font-bold text-[10px] uppercase tracking-widest shadow-lg transition-all"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path></svg>
        INICIO
      </button>

      {/* Banner Premium (El del Mundial) */}
      <div className="relative z-10">
        <MainHeroBanner />
      </div>

      <div className="relative z-10 px-4 py-6 space-y-6">
        {/* Carrito Flotante Minimalista */}
        <div className="flex justify-end mb-4">
          <button onClick={() => { navigate('/catalogo', { replace: true }); navigate('/cart'); }} className="bg-black/40 p-4 rounded-2xl border border-[#39FF14]/20 text-white flex items-center gap-3">
            <ShoppingBag className="w-6 h-6 text-[#39FF14]" />
            <div className="text-left">
              <p className="text-[10px] text-gray-400 font-bold uppercase">Mi Carrito</p>
              <p className="text-sm font-black text-white">{cartCount} Ítems</p>
            </div>
          </button>
        </div>

        <SearchBar value={searchQuery} onChange={setSearchQuery} onClear={() => setSearchQuery('')} placeholder="Buscar por equipo..." />
        
        <div className="flex flex-col gap-4">
          <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-none snap-x h-16 items-center">
            {categoryGroups.map(group => (
              <button
                key={group.id}
                onClick={() => { setActiveGroup(group.id); if (group.id === 'all') { setSelectedCategory(null); setShowOferta(false); } }}
                className={`flex items-center gap-2 px-6 py-3 rounded-2xl font-black text-xs transition-all snap-start flex-shrink-0 uppercase tracking-widest ${
                  activeGroup === group.id && !showOferta && !showDescuento
                    ? 'bg-[#39FF14] text-black shadow-[0_0_20px_#39FF14]'
                    : 'bg-[#1A1A1A] text-gray-400 border border-gray-800'
                }`}
              >
                {group.icon} {group.label}
              </button>
            ))}
            
            <button
              onClick={() => { 
                setShowOferta(!showOferta); 
                setShowDescuento(false);
                setActiveGroup(showOferta ? 'all' : 'oferta'); 
                setSelectedCategory(null);
              }}
              className={`flex items-center gap-2 px-6 py-3 rounded-2xl font-black text-xs transition-all snap-start flex-shrink-0 uppercase tracking-widest ${
                showOferta 
                  ? 'bg-gradient-to-r from-[#FF003C] to-[#FF6B00] text-white shadow-[0_0_20px_#FF003C]' 
                  : 'bg-[#1A1A1A] text-[#FF003C] border border-[#FF003C]/30'
              }`}
            >
              <Star className="w-4 h-4 fill-current" /> OFERTAS
            </button>

            <button
              onClick={() => { 
                setShowDescuento(!showDescuento); 
                setShowOferta(false);
                setActiveGroup(showDescuento ? 'all' : 'descuento'); 
                setSelectedCategory(null);
              }}
              className={`flex items-center gap-2 px-6 py-3 rounded-2xl font-black text-xs transition-all snap-start flex-shrink-0 uppercase tracking-widest ${
                showDescuento 
                  ? 'bg-[#39FF14] text-black shadow-[0_0_20px_#39FF14]' 
                  : 'bg-[#1A1A1A] text-[#39FF14] border border-[#39FF14]/30'
              }`}
            >
              <Star className="w-4 h-4 fill-current" /> DESCUENTOS %
            </button>
          </div>

          {activeGroup !== 'all' && activeGroup !== 'oferta' && activeGroup !== 'descuento' && (
            <div className="flex flex-col gap-2">
              <p className="text-[10px] text-[#39FF14] font-black uppercase tracking-[0.2em] ml-1">Subcategorías</p>
              <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-none snap-x">
                {categoryGroups.find(g => g.id === activeGroup)?.items.map(cat => (
                  <button
                    key={cat || 'all'}
                    onClick={() => { setSelectedCategory(cat); setShowOferta(false); }}
                    className={`px-5 py-2.5 rounded-xl text-xs font-black whitespace-nowrap transition-all snap-start border uppercase tracking-wider ${
                      selectedCategory === cat 
                        ? 'bg-white text-black border-white shadow-[0_0_15px_rgba(255,255,255,0.3)] scale-105' 
                        : 'bg-[#121212] border-gray-800 text-gray-500 hover:border-gray-600'
                    }`}
                  >
                    {cat === null ? 'VER TODO' : cat}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <SizeFilters selectedSize={selectedSize} onSizeChange={setSelectedSize} />
      </div>

      <div className="px-4 py-2">
        <h2 className="text-xl font-black italic border-l-4 border-[#39FF14] pl-3 mb-6 tracking-tighter uppercase">// Catálogo Oficial</h2>

        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-20 gap-4">
            <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-[#39FF14]"></div>
            <p className="text-[#39FF14] font-bold text-xs tracking-widest uppercase">Cargando Estilo...</p>
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="text-center py-20 text-gray-500 uppercase font-bold tracking-widest">No hay productos disponibles</div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {filteredProducts.map((producto) => (
              <div key={producto.id} className={`bg-[#111] rounded-2xl p-3 border transition-all group ${producto.agotado ? 'border-gray-800 opacity-60' : 'border-gray-800 hover:border-[#39FF14]/30'}`}>
                <div className="relative">
                  <ImageCarousel images={producto.images} productName={producto.descripcion} onClick={() => !producto.agotado && openLightbox(producto)} />
                  {producto.agotado && (
                    <div className="absolute inset-0 bg-black/70 flex items-center justify-center rounded-xl z-10">
                      <span className="text-white font-black text-sm uppercase tracking-widest border-2 border-gray-500 px-3 py-1 rounded-lg bg-black/60">AGOTADO</span>
                    </div>
                  )}
                  {!producto.agotado && producto.oferta && producto.descuento && (
                    <div className="absolute top-2 right-2 bg-[#FF003C] text-white font-black text-[10px] px-2 py-1 rounded-md shadow-lg z-10">
                      -{producto.descuento}%
                    </div>
                  )}
                </div>
                <h3 className="text-white font-bold text-[10px] text-center uppercase mt-3 h-8 line-clamp-2 leading-tight group-hover:text-[#39FF14] transition-colors">{producto.descripcion}</h3>
                <div className="flex flex-col items-center mt-2 gap-0.5">
                  {producto.descuento > 0 ? (() => {
                    const precioFinal = producto.precio_oferta || producto.precio * (1 - producto.descuento / 100);
                    return (
                      <>
                        <div className="flex items-center gap-1">
                          <span className="text-[9px] text-gray-500 line-through font-bold">${producto.precio.toFixed(2)}</span>
                          <p className="text-[#39FF14] font-black text-sm">${precioFinal.toFixed(2)}</p>
                          <span className="text-[9px] text-gray-500 font-bold uppercase">USD</span>
                        </div>
                        {toBolivares(precioFinal) && (
                          <span className="text-[10px] text-gray-400 font-semibold">Bs. {toBolivares(precioFinal).toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                        )}
                      </>
                    );
                  })() : producto.oferta && producto.precio_oferta ? (
                    <>
                      <div className="flex items-center gap-1">
                        <span className="text-[9px] text-gray-500 line-through font-bold">${producto.precio.toFixed(2)}</span>
                        <p className="text-[#FF003C] font-black text-sm">${producto.precio_oferta.toFixed(2)}</p>
                        <span className="text-[9px] text-gray-500 font-bold uppercase">USD</span>
                      </div>
                      {toBolivares(producto.precio_oferta) && (
                        <span className="text-[10px] text-gray-400 font-semibold">Bs. {toBolivares(producto.precio_oferta).toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      )}
                    </>
                  ) : (
                    <>
                      <div className="flex items-center gap-1">
                        <p className="text-white font-black text-sm">${(producto.precio || 0).toFixed(2)}</p>
                        <span className="text-[9px] text-gray-500 font-bold uppercase">USD</span>
                      </div>
                      {toBolivares(producto.precio) && (
                        <span className="text-[10px] text-gray-400 font-semibold">Bs. {toBolivares(producto.precio).toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      )}
                    </>
                  )}
                </div>
                <button
                  onClick={() => !producto.agotado && openLightbox(producto)}
                  disabled={producto.agotado}
                  className={`w-full font-black text-[10px] py-3 rounded-xl mt-4 uppercase tracking-widest transition-all shadow-lg ${
                    producto.agotado
                      ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                      : 'bg-[#39FF14] text-black hover:scale-105 active:scale-95'
                  }`}
                >
                  {producto.agotado ? 'AGOTADO' : 'Ver Detalles'}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <Footer />

      {lightboxProduct && (
        <ProductLightbox
          product={lightboxProduct}
          onClose={closeLightbox}
          onAddToCart={(p, size) => handleAddToCart(p, size)}
        />
      )}
    </div>
  );
}
